import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import { getQuotaKeyForCatalogAvatar } from '@/helpers/product-quota.helper';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';

export type EventCatalogQuotaIconState =
  | 'no_booked'
  | 'some_booked'
  | 'all_booked'
  | 'some_booked_warn'
  | 'all_booked_warn';

/**
 * `catalogProducts` should be the same set shown in the monetize UI (e.g. `filterProductsForEventCatalogUi`),
 * so counts and limits match feature flags, `visible`, and API-backed keys.
 */
export function computeEventCatalogQuotaIconState(
  balances: QuotaBalanceDto[] | undefined,
  catalogProducts: ProductDto[] | undefined,
  event: EventDto,
): EventCatalogQuotaIconState {
  const quotaProducts = catalogProducts?.filter((p) => getQuotaKeyForCatalogAvatar(p) != null) ?? [];
  const totalQuotaProducts = quotaProducts.length;

  const keysWithGrant = new Set((balances ?? []).filter((b) => b.totalGranted > 0).map((b) => b.quotaKey));

  const bookedQuotaProductCount = quotaProducts.filter((p) => {
    const k = getQuotaKeyForCatalogAvatar(p);
    return k && keysWithGrant.has(k);
  }).length;

  const visibleQuotaKeys = new Set(
    quotaProducts.map((p) => getQuotaKeyForCatalogAvatar(p)).filter((k): k is string => Boolean(k)),
  );
  const isApproachingLimit = (balances ?? []).some(
    (b) => visibleQuotaKeys.has(b.quotaKey) && b.totalGranted > 0 && b.available <= 0,
  );

  const hasDraftOrders = event.orders?.some(
    (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
  );

  if (bookedQuotaProductCount === 0 && !hasDraftOrders) return 'no_booked';
  if (totalQuotaProducts > 0 && bookedQuotaProductCount === totalQuotaProducts) {
    return isApproachingLimit ? 'all_booked_warn' : 'all_booked';
  }
  return isApproachingLimit ? 'some_booked_warn' : 'some_booked';
}
